import { CdkTrapFocus, A11yModule } from '@angular/cdk/a11y';
import * as i2 from '@angular/common';
import { DatePipe, CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, HostListener, ViewChild, Output, ViewEncapsulation, Component, Injectable, forwardRef, Injector, Renderer2, ElementRef, Input, Inject, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import { NG_VALUE_ACCESSOR, NgControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { InputSharedPropertiesBase, DirectivesModule } from '@windmill/ng-windmill/shared';
import * as i1 from '@angular/cdk/overlay';
import { OverlayConfig, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

const NEGATIVE_INDEX = -1;
const MIN_STEP = 1;
const MAX_VISIBLE_ITEMS = 5;
const STEP$1 = 60;
const MINUTES_PER_DAY = 1440;
const HOUR_IN_MINUTES = 60;
/**
 * To use this component in a module, you can import the `CentricInputTimepicker2Module`:
 * @example
 * ```typescript
 * import { CentricInputTimepicker2Module } from '@windmill/ng-windmill/input-timepicker';
 * ```
 *
 * To use the standalone component directly, you can import `Timepicker2Component`:
 * @example
 * ```typescript
 * import { Timepicker2Component } from '@windmill/ng-windmill/input-timepicker';
 * ```
 */
class Timepicker2Component {
    /*
     * Emits when selecting a time from the overlay list
     */
    selectedTime = new EventEmitter();
    /*
     * Emits when closing the overlay list
     */
    isClosed = new EventEmitter();
    listOverlay;
    timeFormat = 'HH:mm';
    testListId;
    step = STEP$1;
    min;
    max;
    value;
    focusableElement = 0;
    timeOptions = [];
    liElements;
    ariaLabel;
    ariaLabelledby;
    get hasScrollBar() {
        return this.timeOptions.length > MAX_VISIBLE_ITEMS;
    }
    ngOnInit() {
        this._generateTimeOptions();
    }
    ngAfterViewInit() {
        this.listOverlay.nativeElement.focus();
        this.liElements = this.listOverlay.nativeElement.getElementsByTagName('li');
        const selectedItem = Array.from(this.liElements).find(item => item.classList.contains('is-selected'));
        this.focusableElement = selectedItem ? Array.from(this.liElements).indexOf(selectedItem) : 0;
        this.liElements[this.focusableElement]?.focus();
    }
    onKeyboardChange(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
            this.isClosed.emit();
        }
        if (event.key === ' ') {
            event.preventDefault();
        }
        this.onArrowsNavigation(event);
        return true;
    }
    getTabIndex(position) {
        return position === 0 ? 0 : NEGATIVE_INDEX;
    }
    onArrowsNavigation(event) {
        if (this.shouldFocusNextElement(event)) {
            this.focusableElement += 1;
        }
        if (this.shouldFocusPreviousElement(event)) {
            this.focusableElement -= 1;
        }
        if (['Down', 'ArrowDown', 'Up', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            this.liElements[this.focusableElement].focus();
        }
    }
    selectTime(date) {
        this.selectedTime.emit(date);
    }
    isSelected(date) {
        return this.value ? date.getTime() === this.value.getTime() : false;
    }
    _generateTimeOptions() {
        const optionsStep = this._getOptionsStep();
        const date = this.value ? this.value : this._getInitialDate();
        const optionsCount = MINUTES_PER_DAY / optionsStep;
        this.timeOptions = [];
        this._populateTimeOptions(date, optionsStep, optionsCount);
    }
    _getOptionsStep() {
        if (!this.step || this.step < MIN_STEP) {
            return MIN_STEP;
        }
        return Math.trunc(this.step);
    }
    _populateTimeOptions(date, optionsStep, optionsCount) {
        for (let i = 0; i < optionsCount; i++) {
            const timeOption = this.getNewFormattedDate(date, i * optionsStep);
            if (this._isValidTime(timeOption)) {
                this.timeOptions.push(timeOption);
            }
        }
    }
    _parseTimeString(timeString) {
        const [hours, minutes] = timeString.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) {
            return null;
        }
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    }
    _isValidDate(date) {
        return date instanceof Date && !isNaN(date.getTime());
    }
    _isValidTime(date) {
        const parsedDate = typeof date === 'string' ? this._parseTimeString(date) : date;
        if (!this._isValidDate(parsedDate)) {
            return false;
        }
        return parsedDate !== null && this._isWithinTimeRange(parsedDate);
    }
    _isWithinTimeRange(date) {
        if (this._hasValidMinAndMax()) {
            return this._isWithinMinAndMax(date);
        }
        if (this._hasValidMin()) {
            return this._isAfterMin(date);
        }
        if (this._hasValidMax()) {
            return this._isBeforeMax(date);
        }
        return true;
    }
    _hasValidMinAndMax() {
        return this._hasValidMin() && this._hasValidMax();
    }
    _hasValidMin() {
        return this.min instanceof Date && !isNaN(this.min.getTime());
    }
    _hasValidMax() {
        return this.max instanceof Date && !isNaN(this.max.getTime());
    }
    _isWithinMinAndMax(date) {
        if (this.min && this.max) {
            return this._compareTime(date, this.min) >= 0 && this._compareTime(date, this.max) <= 0;
        }
        return false;
    }
    _isAfterMin(date) {
        if (this.min) {
            return this._compareTime(date, this.min) >= 0;
        }
        return false;
    }
    _isBeforeMax(date) {
        if (this.max) {
            return this._compareTime(date, this.max) <= 0;
        }
        return false;
    }
    _compareTime(firstDate, secondDate) {
        const firstTime = firstDate.getHours() * HOUR_IN_MINUTES + firstDate.getMinutes();
        const secondTime = secondDate.getHours() * HOUR_IN_MINUTES + secondDate.getMinutes();
        return firstTime - secondTime;
    }
    _getInitialDate() {
        const date = new Date();
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }
    getNewFormattedDate(date, minutesCount) {
        const newDate = new Date(date);
        const hours = Math.floor(minutesCount / STEP$1);
        const minutes = minutesCount - hours * STEP$1;
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
        return newDate;
    }
    shouldFocusNextElement(event) {
        const positionLastElement = this.liElements.length - 1;
        return (event.key === 'ArrowDown' || event.key === 'Down') && this.focusableElement < positionLastElement;
    }
    shouldFocusPreviousElement(event) {
        return (event.key === 'ArrowUp' || event.key === 'Up') && this.focusableElement > 0;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Timepicker2Component, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: Timepicker2Component, isStandalone: true, selector: "centric-timepicker", outputs: { selectedTime: "selectedTime", isClosed: "isClosed" }, host: { listeners: { "keydown": "onKeyboardChange($event)" } }, viewQueries: [{ propertyName: "listOverlay", first: true, predicate: ["overlayList"], descendants: true }], ngImport: i0, template: "<section class=\"timepicker-content\">\r\n  <ul\r\n    cdkTrapFocus\r\n    #overlayList\r\n    role=\"listbox\"\r\n    [class.has-scroll-bar]=\"hasScrollBar\"\r\n    [attr.data-testid]=\"testListId\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\">\r\n    @for (option of timeOptions; track option; let position = $index) {\r\n      <li\r\n        class=\"centric-item-timepicker\"\r\n        role=\"option\"\r\n        [class.is-selected]=\"isSelected(option)\"\r\n        [attr.tabindex]=\"getTabIndex(position)\"\r\n        [attr.aria-selected]=\"isSelected(option)\"\r\n        (click)=\"selectTime(option)\"\r\n        (keydown.enter)=\"selectTime(option)\">\r\n        {{ option | date: timeFormat }}\r\n      </li>\r\n    }\r\n  </ul>\r\n</section>\r\n", dependencies: [{ kind: "directive", type: CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "pipe", type: DatePipe, name: "date" }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Timepicker2Component, decorators: [{
            type: Component,
            args: [{ selector: 'centric-timepicker', encapsulation: ViewEncapsulation.None, imports: [CdkTrapFocus, DatePipe], standalone: true, template: "<section class=\"timepicker-content\">\r\n  <ul\r\n    cdkTrapFocus\r\n    #overlayList\r\n    role=\"listbox\"\r\n    [class.has-scroll-bar]=\"hasScrollBar\"\r\n    [attr.data-testid]=\"testListId\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\">\r\n    @for (option of timeOptions; track option; let position = $index) {\r\n      <li\r\n        class=\"centric-item-timepicker\"\r\n        role=\"option\"\r\n        [class.is-selected]=\"isSelected(option)\"\r\n        [attr.tabindex]=\"getTabIndex(position)\"\r\n        [attr.aria-selected]=\"isSelected(option)\"\r\n        (click)=\"selectTime(option)\"\r\n        (keydown.enter)=\"selectTime(option)\">\r\n        {{ option | date: timeFormat }}\r\n      </li>\r\n    }\r\n  </ul>\r\n</section>\r\n" }]
        }], propDecorators: { selectedTime: [{
                type: Output
            }], isClosed: [{
                type: Output
            }], listOverlay: [{
                type: ViewChild,
                args: ['overlayList']
            }], onKeyboardChange: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class Timepicker2Service {
    overlay;
    _datePipe;
    overlayRef;
    elementRef;
    datePipe;
    closed = new EventEmitter();
    positions = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 4
        },
        {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
            offsetY: -4
        }
    ];
    constructor(overlay, _datePipe) {
        this.overlay = overlay;
        this._datePipe = _datePipe;
        this.datePipe = _datePipe;
    }
    openTimepicker(properties) {
        this.elementRef = properties.elementRef;
        this.overlayRef = this.createOverlay(properties.elementRef);
        const componentRef = this.attachTimepicker(this.overlayRef);
        this.initializeTimepickerProperties(componentRef, properties);
        return componentRef;
    }
    closeTimepicker() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            const inputContainer = this.elementRef.nativeElement.getElementsByTagName('input')[0];
            inputContainer.focus();
        }
    }
    attachTimepicker(overlayRef) {
        const dropdownPortal = new ComponentPortal(Timepicker2Component);
        this.overlayRef?.backdropClick().subscribe(() => {
            this.closed.emit(true);
        });
        return overlayRef.attach(dropdownPortal);
    }
    createOverlay(elementRef) {
        const timepickerBlock = elementRef.nativeElement.querySelectorAll('.centric-timepicker-block')[0];
        return this.overlay.create(new OverlayConfig({
            hasBackdrop: true,
            backdropClass: 'transparent-backdrop',
            panelClass: 'timepicker-panel',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(timepickerBlock)
                .withPositions(this.positions)
                .withFlexibleDimensions(false)
                .withPush(false)
        }));
    }
    initializeTimepickerProperties(componentRef, properties) {
        const timepickerComponent = componentRef.instance;
        timepickerComponent.step = properties.step;
        timepickerComponent.value = properties.valueSelected;
        timepickerComponent.testListId = properties.testListId;
        timepickerComponent.timeFormat = properties.timeFormat;
        timepickerComponent.min = properties.min;
        timepickerComponent.max = properties.max;
        timepickerComponent.ariaLabel = properties.ariaLabelList;
        timepickerComponent.ariaLabelledby = properties.ariaLabelledbyList;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Timepicker2Service, deps: [{ token: i1.Overlay }, { token: i2.DatePipe }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Timepicker2Service, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Timepicker2Service, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.Overlay }, { type: i2.DatePipe }] });

const STEP = 60;
const MAX_LENGTH_HALF = 2;
const MAX_LENGTH = 4;
const CENTRIC_INPUT_TIMEPICKER_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputTimepicker2Component),
    multi: true
};
/**
 * To use this component in a module, you can import the `CentricInputTimepicker2Module`:
 * @example
 * ```typescript
 * import { CentricInputTimepicker2Module } from '@windmill/ng-windmill/input-timepicker';
 * ```
 *
 * To use the standalone component directly, you can import `InputTimepicker2Component`:
 * @example
 * ```typescript
 * import { InputTimepicker2Component } from '@windmill/ng-windmill/input-timepicker';
 * ```
 */
class InputTimepicker2Component extends InputSharedPropertiesBase {
    timepicker2Service;
    injector;
    render2;
    elementRef;
    /**
     * Sets the aria-label attribute for the timepicker button
     */
    ariaLabelTimepickerIcon;
    /**
     * Sets the aria-label attribute for the timepicker list
     */
    ariaLabelList;
    /**
     * Sets the aria-labelledby attribute for the timepicker list
     */
    ariaLabelledbyList;
    /*
     * The data-testid for icon from component
     */
    testIconId;
    /*
     * The data-testid for list from component
     */
    testListId;
    /*
     * It is the time format
     */
    timeFormat = 'HH:mm';
    /*
     * Used to display the hour with the recurrence of the value set.
     * By default is 60 minutes.
     */
    timepickerStep = STEP;
    /*
     * Used to set the maximum selectable time.
     */
    max;
    /*
     * Used to set the minimum selectable time.
     */
    min;
    /*
     * Emits a string when change manual the input value
     */
    manualInputChange = new EventEmitter();
    /*
     * Emits when selecting a time from the overlay list
     */
    listSelectionChange = new EventEmitter();
    /**
     * Emits when value of the time picker input will be deleted
     */
    clearValue = new EventEmitter();
    inputElement;
    autocomplete = 'off';
    isTimepickerVisible = false;
    stringValue = null;
    value;
    selectedItemEventSubscription = Subscription.EMPTY;
    isClosedSubscription = Subscription.EMPTY;
    overlayClosedSubscription = Subscription.EMPTY;
    _manualChange = false;
    constructor(timepicker2Service, injector, render2, elementRef) {
        super(render2, elementRef);
        this.timepicker2Service = timepicker2Service;
        this.injector = injector;
        this.render2 = render2;
        this.elementRef = elementRef;
    }
    ngOnDestroy() {
        this.closeVisibleTimepicker();
        this.selectedItemEventSubscription.unsubscribe();
        this.isClosedSubscription.unsubscribe();
        this.overlayClosedSubscription.unsubscribe();
    }
    cleanInput(event) {
        if (event.key.replace(/[^0-9]/g, '') === '' && event.target.nodeName === 'INPUT') {
            event.preventDefault();
        }
        return true;
    }
    onResize() {
        if (this.isTimepickerVisible) {
            this.isTimepickerVisible = false;
            this.timepicker2Service.closeTimepicker();
        }
    }
    onPopState() {
        this.closeVisibleTimepicker();
    }
    writeValue(value) {
        this.value = value;
        if (value) {
            this.stringValue = this._manualChange
                ? this.stringValue
                : this.timepicker2Service.datePipe.transform(value, this.timeFormat);
        }
        else {
            this.stringValue = null;
        }
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    getPlaceholder() {
        const placeholderValue = typeof this.placeholder === 'undefined' ? null : this.placeholder;
        return this.hasValue ? null : this.readonly ? '-' : placeholderValue;
    }
    closeVisibleTimepicker() {
        if (this.isTimepickerVisible) {
            this.getFormControl().markAsTouched();
            this.closeTimepicker();
        }
    }
    onChangeModel(event) {
        this._manualChange = true;
        this.onModelChangeValue(event);
        this.manualInputChange.emit(event.target.value);
    }
    onModelChangeValue(event) {
        const htmlInputElement = event.target;
        htmlInputElement.value = htmlInputElement.value.replace(':', '');
        this.stringValue = htmlInputElement.value;
        this.updateDate(htmlInputElement.value);
        event.stopPropagation();
    }
    updateDate(value) {
        const newDate = this.getNewFormattedDate(value);
        const length = this.stringValue ? this.stringValue?.replace(':', '').length : 0;
        if (length >= MAX_LENGTH) {
            this.stringValue = this.timepicker2Service.datePipe.transform(newDate, this.timeFormat);
        }
        this.updateFormControlValue(this.stringValue ? newDate : null);
    }
    onBlur() {
        this.onTouched();
        this._manualChange = false;
        if (!this.stringValue) {
            this.updateFormControlValue(null);
            return;
        }
        this.updateWithFormattedValue();
    }
    toggleTimepicker() {
        if (!this.disabled && !this.isTimepickerVisible) {
            const timepickerProperties = this.createTimepickerPropertiesObject();
            const timepickerComponentRef = this.timepicker2Service.openTimepicker(timepickerProperties);
            this.isTimepickerVisible = true;
            this.subscribeToSelectedTimeEvent(timepickerComponentRef);
            this.subscribeToIsClosedEvent(timepickerComponentRef);
            this.subscribeToOverlayClosed();
        }
        else {
            this.closeTimepicker();
        }
    }
    closeTimepicker() {
        this.timepicker2Service.closeTimepicker();
        this.isTimepickerVisible = false;
    }
    onClearValue() {
        this.getFormControl().markAsDirty();
        this.getFormControl().patchValue(null);
        this.stringValue = '';
        this.elementRef.nativeElement.getElementsByTagName('input')[0].focus();
        this.isTimepickerVisible = false;
        this.clearValue.emit(this.value);
    }
    onPasteEvent(event) {
        event.preventDefault();
    }
    subscribeToOverlayClosed() {
        this.overlayClosedSubscription = this.timepicker2Service.closed.subscribe(() => {
            if (!this.disabled && this.isTimepickerVisible) {
                this.isTimepickerVisible = false;
                this.onTouched();
            }
            this.timepicker2Service.closeTimepicker();
        });
    }
    get isInvalidTimepicker() {
        if (typeof this.validationFunction !== 'undefined') {
            return this.validationFunction();
        }
        return false;
    }
    get isRequired() {
        return this.getFormControl().hasValidator(Validators.required) && !this.disabled && !this.readonly;
    }
    get isInvalid() {
        return this.isInvalidTimepicker && !this.disabled && !this.readonly;
    }
    get hasValue() {
        return typeof this.value !== 'undefined' && this.value !== null && this.value.toString() !== '';
    }
    get showClearButton() {
        return Boolean(this.hasClearIcon && this.stringValue && !this.disabled);
    }
    createTimepickerPropertiesObject() {
        return {
            step: this.timepickerStep,
            valueSelected: this.value,
            elementRef: this.elementRef,
            testListId: this.testListId,
            timeFormat: this.timeFormat,
            min: this.min,
            max: this.max,
            ariaLabelList: this.ariaLabelList,
            ariaLabelledbyList: this.ariaLabelledbyList
        };
    }
    subscribeToSelectedTimeEvent(timepickerComponentRef) {
        this.selectedItemEventSubscription = timepickerComponentRef.instance.selectedTime.subscribe((data) => {
            this.listSelectionChange.emit(data);
            this.stringValue = this.timepicker2Service.datePipe.transform(data, this.timeFormat);
            this.updateFormControlValue(data);
            this.closeTimepicker();
        });
    }
    subscribeToIsClosedEvent(timepickerComponentRef) {
        this.isClosedSubscription = timepickerComponentRef.instance.isClosed.subscribe(() => {
            this.closeTimepicker();
        });
    }
    updateWithFormattedValue() {
        const oldValue = this.stringValue?.replace(':', '');
        const length = oldValue ? oldValue?.length : 0;
        if (length >= MAX_LENGTH) {
            return;
        }
        const newDate = this.getNewFormattedDate(oldValue);
        this.stringValue = this.timepicker2Service.datePipe.transform(newDate, this.timeFormat);
        this.updateFormControlValue(newDate);
    }
    getNewFormattedDate(value) {
        const [hour, minute] = [Number(value?.slice(0, MAX_LENGTH_HALF)), Number(value?.slice(MAX_LENGTH_HALF, MAX_LENGTH))];
        const baseDate = this.value ? new Date(this.value) : new Date();
        const [year, month, date] = [baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()];
        baseDate.setHours(hour, minute, 0, 0);
        baseDate.setDate(new Date().getDate());
        this.min?.setFullYear(year, month, date);
        this.max?.setFullYear(year, month, date);
        return baseDate;
    }
    updateFormControlValue(value) {
        this.writeValue(value);
        this.onChange(value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputTimepicker2Component, deps: [{ token: Timepicker2Service }, { token: forwardRef(() => Injector) }, { token: forwardRef(() => Renderer2) }, { token: forwardRef(() => ElementRef) }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: InputTimepicker2Component, isStandalone: true, selector: "centric-input-timepicker2", inputs: { ariaLabelTimepickerIcon: "ariaLabelTimepickerIcon", ariaLabelList: "ariaLabelList", ariaLabelledbyList: "ariaLabelledbyList", testIconId: "testIconId", testListId: "testListId", timeFormat: "timeFormat", timepickerStep: "timepickerStep", max: "max", min: "min" }, outputs: { manualInputChange: "manualInputChange", listSelectionChange: "listSelectionChange", clearValue: "clearValue" }, host: { listeners: { "keypress": "cleanInput($event)", "window:resize": "onResize()", "window:popstate": "onPopState()" } }, providers: [CENTRIC_INPUT_TIMEPICKER_VALUE_ACCESSOR, DatePipe, Timepicker2Service], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["inputElement"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\r\n  class=\"windmill-input centric-timepicker-block\"\r\n  [ngClass]=\"size\"\r\n  [class.is-invalid]=\"isInvalidTimepicker\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [class.has-value]=\"value\">\r\n  <input\r\n    class=\"input-element\"\r\n    #inputElement\r\n    maxlength=\"4\"\r\n    [ngModel]=\"stringValue\"\r\n    [value]=\"stringValue\"\r\n    [readonly]=\"readonly\"\r\n    [disabled]=\"disabled\"\r\n    [autocomplete]=\"autocomplete\"\r\n    [attr.placeholder]=\"getPlaceholder()\"\r\n    [attr.id]=\"id\"\r\n    [attr.title]=\"title\"\r\n    [attr.data-testid]=\"testInputId\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [attr.aria-disabled]=\"disabled ? true : null\"\r\n    [attr.aria-required]=\"isRequired\"\r\n    [attr.aria-invalid]=\"isInvalid\"\r\n    (paste)=\"onPasteEvent($event)\"\r\n    (input)=\"onChangeModel($event)\"\r\n    (blur)=\"onBlur()\" />\r\n  @if (!readonly) {\r\n    <div class=\"icon-group\">\r\n      @if (showClearButton) {\r\n        <button\r\n          type=\"button\"\r\n          class=\"clear-icon-button\"\r\n          [attr.aria-label]=\"ariaLabelClearIcon\"\r\n          (click)=\"onClearValue()\">\r\n          <centric-icon2\r\n            class=\"clear-icon\"\r\n            [svgIcon]=\"'cancel_r'\"\r\n            [size]=\"'16'\">\r\n          </centric-icon2>\r\n        </button>\r\n      }\r\n      <button\r\n        class=\"centric-timepicker-icon-button\"\r\n        type=\"button\"\r\n        aria-haspopup=\"listbox\"\r\n        [attr.disabled]=\"disabled ? true : null\"\r\n        [attr.aria-label]=\"ariaLabelTimepickerIcon\"\r\n        [attr.aria-expanded]=\"isTimepickerVisible ? true : null\"\r\n        (click)=\"toggleTimepicker()\">\r\n        <centric-icon2\r\n          [size]=\"'24'\"\r\n          [svgIcon]=\"'clock_b'\"\r\n          [attr.data-testid]=\"testIconId\">\r\n        </centric-icon2>\r\n      </button>\r\n    </div>\r\n  }\r\n</div>\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (isInvalidTimepicker) {\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputTimepicker2Component, decorators: [{
            type: Component,
            args: [{ selector: 'centric-input-timepicker2', encapsulation: ViewEncapsulation.None, imports: [A11yModule, CommonModule, FormsModule, NgClass, ReactiveFormsModule, CentricIcon2Component], providers: [CENTRIC_INPUT_TIMEPICKER_VALUE_ACCESSOR, DatePipe, Timepicker2Service], standalone: true, template: "<div\r\n  class=\"windmill-input centric-timepicker-block\"\r\n  [ngClass]=\"size\"\r\n  [class.is-invalid]=\"isInvalidTimepicker\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [class.has-value]=\"value\">\r\n  <input\r\n    class=\"input-element\"\r\n    #inputElement\r\n    maxlength=\"4\"\r\n    [ngModel]=\"stringValue\"\r\n    [value]=\"stringValue\"\r\n    [readonly]=\"readonly\"\r\n    [disabled]=\"disabled\"\r\n    [autocomplete]=\"autocomplete\"\r\n    [attr.placeholder]=\"getPlaceholder()\"\r\n    [attr.id]=\"id\"\r\n    [attr.title]=\"title\"\r\n    [attr.data-testid]=\"testInputId\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [attr.aria-disabled]=\"disabled ? true : null\"\r\n    [attr.aria-required]=\"isRequired\"\r\n    [attr.aria-invalid]=\"isInvalid\"\r\n    (paste)=\"onPasteEvent($event)\"\r\n    (input)=\"onChangeModel($event)\"\r\n    (blur)=\"onBlur()\" />\r\n  @if (!readonly) {\r\n    <div class=\"icon-group\">\r\n      @if (showClearButton) {\r\n        <button\r\n          type=\"button\"\r\n          class=\"clear-icon-button\"\r\n          [attr.aria-label]=\"ariaLabelClearIcon\"\r\n          (click)=\"onClearValue()\">\r\n          <centric-icon2\r\n            class=\"clear-icon\"\r\n            [svgIcon]=\"'cancel_r'\"\r\n            [size]=\"'16'\">\r\n          </centric-icon2>\r\n        </button>\r\n      }\r\n      <button\r\n        class=\"centric-timepicker-icon-button\"\r\n        type=\"button\"\r\n        aria-haspopup=\"listbox\"\r\n        [attr.disabled]=\"disabled ? true : null\"\r\n        [attr.aria-label]=\"ariaLabelTimepickerIcon\"\r\n        [attr.aria-expanded]=\"isTimepickerVisible ? true : null\"\r\n        (click)=\"toggleTimepicker()\">\r\n        <centric-icon2\r\n          [size]=\"'24'\"\r\n          [svgIcon]=\"'clock_b'\"\r\n          [attr.data-testid]=\"testIconId\">\r\n        </centric-icon2>\r\n      </button>\r\n    </div>\r\n  }\r\n</div>\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (isInvalidTimepicker) {\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  }\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: Timepicker2Service }, { type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }], propDecorators: { ariaLabelTimepickerIcon: [{
                type: Input
            }], ariaLabelList: [{
                type: Input
            }], ariaLabelledbyList: [{
                type: Input
            }], testIconId: [{
                type: Input
            }], testListId: [{
                type: Input
            }], timeFormat: [{
                type: Input
            }], timepickerStep: [{
                type: Input
            }], max: [{
                type: Input
            }], min: [{
                type: Input
            }], manualInputChange: [{
                type: Output
            }], listSelectionChange: [{
                type: Output
            }], clearValue: [{
                type: Output
            }], inputElement: [{
                type: ViewChild,
                args: ['inputElement']
            }], cleanInput: [{
                type: HostListener,
                args: ['keypress', ['$event']]
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }], onPopState: [{
                type: HostListener,
                args: ['window:popstate']
            }] } });

const windmillModules = [CentricIcon2Module, DirectivesModule];
const moduleComponents = [InputTimepicker2Component, Timepicker2Component];
class CentricInputTimepicker2Module {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricInputTimepicker2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricInputTimepicker2Module, imports: [A11yModule, CommonModule, FormsModule, ReactiveFormsModule, CentricIcon2Module, DirectivesModule, InputTimepicker2Component, Timepicker2Component], exports: [InputTimepicker2Component, Timepicker2Component] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricInputTimepicker2Module, providers: [DatePipe, Overlay], imports: [A11yModule, CommonModule, FormsModule, ReactiveFormsModule, windmillModules, InputTimepicker2Component] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricInputTimepicker2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [A11yModule, CommonModule, FormsModule, ReactiveFormsModule, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents],
                    providers: [DatePipe, Overlay],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricInputTimepicker2Module, InputTimepicker2Component, Timepicker2Component };
